/**
 * Merge jar creatures as layered cartoon sprites, so the light stays upper left while a body
 * rolls: the disc (upright) + the creature (turned with `body.angle`, swapped for its blink
 * picture now and then) + the disc's glint (upright), all in the `tier/<assetId>` frame and
 * placed with exactly `MergeAssets.layout`'s size and anchor (the art fills the physics circle).
 * On top of that, `body-life.js` adds a drawn-only squash / stretch / pop transform.
 *
 * Pixi: three pooled sprites per body (`merge-<id>-d|b|s`); Canvas 2D: the same three images
 * through one transform. Without the layer canvases (no 2D canvas, e.g. tests) a body falls
 * back to the single composite tier sprite. The layers are never drawn inside a frame: a tier
 * whose layers are not baked yet (MergeAssets.peekLayer queues them for idle time) shows the
 * composite for those few frames, and a missing blink just keeps the eyes open.
 *
 * Bodies are drawn a little larger than their physics circle (displayBoost): the glass rim lands
 * at JAR_DISPLAY_RIM × the radius, so resting neighbours overlap a hair instead of showing gaps
 * (the rim sits inside the art frame, and the idle wobble would open them further). Drawing only.
 *
 * Layers are sized from the logical tier frame (MergeAssets.frame), not from their pixels: each
 * tier is baked at a scale that fits how big it is shown (reported here per body), so a layer
 * canvas may be finer or coarser than the composite and still stacks exactly.
 */
import { MergeAssets } from "../../../assets/merge-assets.js";
import { prefersReducedMotion } from "../../../core/sky-background-prefs.js";
import { createBodyLife } from "./body-life.js";
import { EVOLUTION_CHAIN } from "../../../config/constants.js";
import { TIER_ART_SIZES, creatureDiscRadius } from "../art-catalog.js";
import { releaseCanvasTexture } from "./texture-hints.js";

/** World z of merge bodies (the jar glass front sits at 18 in the FX layer above). */
var BODY_Z = 20;

/** Where the drawn glass rim lands, as a share of the physics radius. */
export var JAR_DISPLAY_RIM = 1.012;

var boostByTier = Object.create(null);

/**
 * Drawn-radius factor for a tier: the art maps the frame's long side to the physics diameter,
 * and the disc inside that frame is a little smaller, so scale it out to JAR_DISPLAY_RIM.
 */
export function displayBoost(tier) {
  if (boostByTier[tier] != null) return boostByTier[tier];
  var cfg = EVOLUTION_CHAIN[tier];
  var size = cfg && TIER_ART_SIZES[cfg.assetId];
  var k = 1;
  if (size) {
    var discShare = (2 * creatureDiscRadius(size[0], size[1])) / Math.max(size[0], size[1]);
    if (discShare > 0) k = JAR_DISPLAY_RIM / discShare;
  }
  boostByTier[tier] = k;
  return k;
}

function nowSec() {
  return (typeof performance !== "undefined" && performance.now ? performance.now() : Date.now()) / 1000;
}

/**
 * @typedef {Object} JarBodyFrame
 * @property {number} [animTime] pause-aware seconds (the juice clock, so slow motion applies)
 * @property {number} [unitRadius] tier-0 radius (px)
 * @property {object[]} [mergeMotions] FlaskSystem#activeMergeMotions (read only)
 * @property {{ getMergePop?: (meta: object) => number }} [juice]
 */

export function createJarBodyDrawer() {
  var life = createBodyLife();
  var mergeById = Object.create(null);
  var juice = null;
  var reduced = false;
  var reducedAt = -1e9;
  var warmed = Object.create(null);
  // Sprite keys per body id, built once (not per body per frame); unseen ids are dropped in end().
  var keyMemo = new Map();
  var keyGen = 0;
  var compositeFreed = Object.create(null);

  function warmTier(tier) {
    if (warmed[tier] || typeof MergeAssets.prewarmLayers !== "function") return;
    warmed[tier] = true;
    MergeAssets.prewarmLayers(tier);
    // The creature a merge of this tier will make (sized from the jar's radius table).
    MergeAssets.prewarmLayers(tier + 1);
    if (typeof MergeAssets.prewarmUrl === "function") MergeAssets.prewarmUrl(tier + 1);
  }

  /** Never draws: a layer not baked yet is queued for idle time and comes back null. */
  function layerOf(tier, name) {
    if (typeof MergeAssets.peekLayer === "function") return MergeAssets.peekLayer(tier, name);
    return typeof MergeAssets.layer === "function" ? MergeAssets.layer(tier, name) : null;
  }

  function keysFor(id) {
    var k = keyMemo.get(id);
    if (!k) {
      var base = "merge-" + id;
      k = { base: base, d: base + "-d", b: base + "-b", s: base + "-s", gen: 0 };
      keyMemo.set(id, k);
    }
    k.gen = keyGen;
    return k;
  }

  /** Report the body's on-screen size so its tier is baked fine enough (and no finer). */
  function noteSize(tier, L, deviceScale) {
    if (typeof MergeAssets.noteLayerSize !== "function") return;
    MergeAssets.noteLayerSize(tier, Math.max(L.width, L.height) * deviceScale, "jar");
  }

  /** Logical frame of a layer (falls back to the picture's own size without a catalog entry). */
  function frameOf(tier, name, img) {
    var f = typeof MergeAssets.frame === "function" ? MergeAssets.frame(tier, name) : null;
    return f || { w: img.naturalWidth || img.width || 1, h: img.naturalHeight || img.height || 1 };
  }

  /**
   * Once a tier draws as layers, its composite picture (shown while they were baking) is not
   * drawn again: free its GPU texture (a no-op when it was never uploaded, or on Canvas 2D).
   */
  function freeComposite(tier, image) {
    if (compositeFreed[tier] || !image) return;
    compositeFreed[tier] = true;
    releaseCanvasTexture(image);
  }

  /** The three layers of a body, or null when the disc or creature is not baked yet. */
  function layersOf(tier, blink) {
    var disc = layerOf(tier, "disc");
    var creature = (blink && layerOf(tier, "blink")) || layerOf(tier, "body");
    var shine = layerOf(tier, "shine");
    if (!disc || !creature) return null;
    return { disc: disc, creature: creature, shine: shine };
  }

  return {
    /** @param {JarBodyFrame} frame */
    begin: function (frame) {
      var f = frame || {};
      var t = f.animTime != null && isFinite(f.animTime) ? f.animTime : nowSec();
      var wall = nowSec();
      if (wall - reducedAt > 2) {
        reducedAt = wall;
        reduced = prefersReducedMotion();
      }
      life.begin(t, f.unitRadius > 0 ? f.unitRadius : 20);
      keyGen++;
      juice = f.juice || null;
      mergeById = Object.create(null);
      var list = f.mergeMotions;
      if (list && list.length) {
        for (var i = 0; i < list.length; i++) {
          var anim = list[i];
          if (!anim || !anim.a || !anim.b) continue;
          mergeById[anim.a.id] = anim;
          mergeById[anim.b.id] = anim;
        }
      }
    },

    /**
     * Pose for one body (call once per body per frame, before drawing it).
     * @param {{ body: object, tier: number }} meta
     * @param {number} radiusX drawn radii (shrink tween applied, merge scale not)
     * @param {number} radiusY
     * @param {{ scale?: number, alpha?: number }|null} motion physics merge tween output
     * @param {boolean} shrinking
     */
    pose: function (meta, radiusX, radiusY, motion, shrinking) {
      var body = meta.body;
      var anim = mergeById[body.id] || null;
      var merging = !!(anim || motion);
      var pop = 1;
      if (!merging && juice && typeof juice.getMergePop === "function") {
        pop = juice.getMergePop(meta);
      }
      return life.pose(meta, Math.max(radiusX, radiusY) * displayBoost(meta.tier), {
        merge: merging ? { anim: anim, motion: motion } : null,
        shrinking: !!shrinking,
        pop: pop,
        reduced: reduced,
      });
    },

    /**
     * Publish one body's layers as Pixi sprites.
     * @returns {boolean} false when the art is not available (caller falls back)
     */
    publish: function (playfield, origin, meta, index, radiusX, radiusY, pose) {
      var body = meta.body;
      var tier = meta.tier;
      var boost = displayBoost(tier);
      radiusX *= boost;
      radiusY *= boost;
      var L = MergeAssets.layout(body.position.x, body.position.y, radiusX, tier, body.angle, radiusY);
      if (!L) return false;
      warmTier(tier);
      noteSize(tier, L, playfield.resolution > 0 ? playfield.resolution : 1);
      var x = origin.x + L.x + pose.dx;
      var y = origin.y + L.y + pose.dy;
      var deform = pose.identity ? null : pose.m;
      var z = BODY_Z + (pose.lift ? 1 : 0) + index * 0.002;
      var keys = keysFor(body.id);
      var art = layersOf(tier, pose.blink);
      if (!art) {
        playfield.sprite({
          key: keys.base,
          image: L.image,
          x: x,
          y: y,
          width: L.width,
          height: L.height,
          rotation: L.rotation,
          alpha: pose.alpha,
          anchorX: L.anchorX,
          anchorY: L.anchorY,
          zIndex: z,
          deform: deform,
        });
        return true;
      }
      freeComposite(tier, L.image);
      // Logical frame → on-screen px (the same factor for every layer and the composite).
      var k = L.width / frameOf(tier, null, L.image).w;
      var fd = frameOf(tier, "disc", art.disc);
      var fb = frameOf(tier, "body", art.creature);
      playfield.sprite({
        key: keys.d, image: art.disc, x: x, y: y,
        width: fd.w * k, height: fd.h * k, rotation: 0,
        alpha: pose.alpha, anchorX: L.anchorX, anchorY: L.anchorY, zIndex: z, deform: deform,
      });
      playfield.sprite({
        key: keys.b, image: art.creature, x: x, y: y,
        width: fb.w * k, height: fb.h * k, rotation: L.rotation,
        alpha: pose.alpha, anchorX: L.anchorX, anchorY: L.anchorY, zIndex: z + 0.0005, deform: deform,
      });
      if (art.shine) {
        var fs = frameOf(tier, "shine", art.shine);
        playfield.sprite({
          key: keys.s, image: art.shine, x: x, y: y,
          width: fs.w * k, height: fs.h * k, rotation: 0,
          alpha: pose.alpha, anchorX: 0.5, anchorY: 0.5, zIndex: z + 0.001, deform: deform,
        });
      }
      return true;
    },

    /**
     * Draw one body's layers on a 2D context (flask-panel space, already translated).
     * @returns {boolean} false when the art is not available (caller falls back)
     */
    drawCanvas: function (ctx, meta, radiusX, radiusY, pose) {
      if (!ctx || typeof ctx.drawImage !== "function") return false;
      var body = meta.body;
      var tier = meta.tier;
      var boost = displayBoost(tier);
      radiusX *= boost;
      radiusY *= boost;
      var L = MergeAssets.layout(body.position.x, body.position.y, radiusX, tier, body.angle, radiusY);
      if (!L) return false;
      warmTier(tier);
      noteSize(tier, L, typeof MergeAssets.canvasDeviceScale === "function" ? MergeAssets.canvasDeviceScale() : 1);
      var art = layersOf(tier, pose.blink);
      var k = L.width / frameOf(tier, null, L.image).w;
      ctx.save();
      ctx.imageSmoothingEnabled = true;
      if (ctx.imageSmoothingQuality != null) ctx.imageSmoothingQuality = "high";
      if (pose.alpha < 1) ctx.globalAlpha *= Math.max(0, pose.alpha);
      ctx.translate(L.x + pose.dx, L.y + pose.dy);
      if (!pose.identity) ctx.transform(pose.m[0], pose.m[1], pose.m[2], pose.m[3], 0, 0);
      function put(img, name, rot, ax, ay) {
        var f = name ? frameOf(tier, name, img) : { w: L.width / k, h: L.height / k };
        var w = f.w * k, h = f.h * k;
        if (rot) {
          ctx.save();
          ctx.rotate(rot);
          ctx.drawImage(img, -ax * w, -ay * h, w, h);
          ctx.restore();
        } else {
          ctx.drawImage(img, -ax * w, -ay * h, w, h);
        }
      }
      if (art) {
        put(art.disc, "disc", 0, L.anchorX, L.anchorY);
        put(art.creature, "body", L.rotation, L.anchorX, L.anchorY);
        if (art.shine) put(art.shine, "shine", 0, 0.5, 0.5);
      } else {
        put(L.image, null, L.rotation, L.anchorX, L.anchorY);
      }
      ctx.restore();
      return true;
    },

    end: function () {
      life.end();
      if (keyMemo.size) {
        keyMemo.forEach(function (k, id) {
          if (k.gen !== keyGen) keyMemo.delete(id);
        });
      }
    },
  };
}

var shared = null;

/** The drawer the jar renderer uses (one per page: per-body state lives across frames). */
export function getJarBodyDrawer() {
  if (!shared) shared = createJarBodyDrawer();
  return shared;
}
